import { Rocket, Check } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import type { ProposedDDQuestion, SuggestedQuestion } from "../types";

interface ProposedDDQuestionsProps {
  proposals: ProposedDDQuestion[];
  fileId: string;
  onAcceptProposal: (
    labelName: string,
    resourceType: 'building' | 'unit',
    suggestedQuestions: SuggestedQuestion[],
    fileId: string
  ) => void;
}

export function ProposedDDQuestions({ proposals, fileId, onAcceptProposal }: ProposedDDQuestionsProps) {
  if (!proposals.length) return null;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-200">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Rocket className="h-3.5 w-3.5 text-amber-600" weight="duotone" />
        <span className="text-[10px] text-amber-600 font-medium">
          Voorgestelde nieuwe DD vragen:
        </span>
      </div>
      <div className="space-y-1.5">
        {proposals.slice(0, 2).map((proposal, idx) => (
          <div
            key={idx}
            className="p-2 bg-amber-50 rounded-md border border-amber-100"
          >
            <p className="text-xs text-neutral-700 font-medium">
              {proposal.questionText}
            </p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-[10px] text-neutral-500 truncate cursor-help mt-0.5">
                    {proposal.reason}
                  </p>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm">
                  <p className="text-xs">{proposal.reason}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">
                    Categorie: {proposal.categoryName || 'Onbekend'} | Scope: {proposal.scope === 'unit' ? 'Unit' : 'Gebouw'} | Type: {proposal.answerType}
                  </p>
                  {proposal.suggestedAnswerOptions && (
                    <div className="mt-1.5">
                      <p className="text-[10px] font-medium">Voorgestelde antwoorden:</p>
                      {proposal.suggestedAnswerOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-1 mt-0.5">
                          <span className={`w-2 h-2 rounded-full ${
                            opt.statusColor === 'green' ? 'bg-green-500' :
                            opt.statusColor === 'red' ? 'bg-red-500' : 'bg-amber-500'
                          }`} />
                          <span className="text-[10px]">{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-200 text-amber-700">
                  {proposal.categoryName || 'Onbekend'}
                </Badge>
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-blue-200 text-blue-700">
                  {proposal.scope === 'unit' ? 'Unit' : 'Gebouw'}
                </Badge>
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-200 text-amber-700">
                  {proposal.answerType === 'options' ? 'Keuze' :
                   proposal.answerType === 'free_text' ? 'Tekst' :
                   proposal.answerType === 'date' ? 'Datum' : 'Nummer'}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-5 text-[9px] px-2 bg-amber-500 hover:bg-amber-600 text-white border-0"
                onClick={(e) => {
                  e.stopPropagation();
                  const suggestedQuestion: SuggestedQuestion = {
                    fieldName: proposal.questionText.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, ''),
                    displayName: proposal.questionText,
                    helpText: proposal.reason,
                    answerType: proposal.answerType === 'options' ? 'options' :
                                proposal.answerType === 'free_text' ? 'free_text' :
                                proposal.answerType === 'date' ? 'date' : 'number',
                    evaluationMode: 'file_based',
                    answerOptions: proposal.suggestedAnswerOptions?.map((opt, optIdx) => ({
                      value: opt.value,
                      label: opt.label,
                      statusColor: opt.statusColor === 'green' ? 'green' :
                                   opt.statusColor === 'red' ? 'red' : 'orange',
                      tooltipLabel: opt.conditionDescription,
                      displayOrder: optIdx,
                    })),
                  };
                  onAcceptProposal(
                    proposal.linkedLabelSuggestion || proposal.questionText,
                    proposal.scope === 'unit' ? 'unit' : 'building',
                    [suggestedQuestion],
                    fileId
                  );
                }}
              >
                <Check className="h-3 w-3 mr-1" />
                Accepteren
              </Button>
            </div>
          </div>
        ))}
        {proposals.length > 2 && (
          <p className="text-[10px] text-amber-500 italic">
            + {proposals.length - 2} meer voorstellen...
          </p>
        )}
      </div>
    </div>
  );
}
